import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { isEmpty } from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";
import { distinctUntilChanged, skip } from "rxjs/operators";
import { CustomValidationService } from "src/app/cbta-web/services/custom-validation.service";
import { CONSTANT } from "src/app/constant";
import { StaffDetails } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { SharedService } from "src/app/shared/services/shared.service";
import {
	clearStaffLookupFailureOnValueChange,
	patchStaffLicenseExpiryDate,
	rejectInstructorOwnStaffNumber,
	staffLookupRolesForForm,
} from "src/app/shared/services/staff-lookup.helper";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

type SessionItem = { code: string; description: string };
type SessionGroupVM = { code: string; key: string; items: SessionItem[] };

@Component({
	selector: "app-candidate-details-group-session",
	templateUrl: "./candidate-details-group-session.component.html",
	styleUrls: ["./candidate-details-group-session.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateDetailsGroupSessionComponent implements OnInit {
	// -------- State --------
	sessions!: any;

	@Input() excludeLicense: boolean = false;

	candidateForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex = 0;
	formType!: string | undefined;

	panelOpenState = false;
	currentDate: Date = new Date();
	DATE_FORMAT = CONSTANT.DATE_FORMAT;
	today: Date = new Date();
	isOnline = true;

	// Guard to prevent autosave during initial wiring
	private isBootstrapping = true;

	// Display/layout (parity with Create Form)
	sessionGroups: SessionGroupVM[] = [];
	singleCols: SessionItem[][] = [];
	twoColsLeft?: SessionGroupVM;
	twoColsRight: SessionItem[][] = [];
	secondGroupTitle = "";
	twoGroupsCompact = false;

	lastColTitle = ""; // 2-group layout: right title           // 2-group layout: right has <= 4 items
	lastCols?: SessionItem[][] = [];

	private notReqSub?: Subscription;
	private otherSessionSubs: Subscription[] = [];

	// Datepicker mirror
	datePickerRef?: ElementRef;
	datePickerInput = new UntypedFormControl(new Date(), { updateOn: "blur" });

	constructor(
		public sharedService: SharedService,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private cvs: CustomValidationService,
		private cd: ChangeDetectorRef,
		public containerRatingService: ContainerRatingsService,
		private profileService: ProfileService,
	) {}

	// -------- Inputs --------

	@Input("candidateForm")
	set _(value: any) {
		this.formType = this.containerRatingService.formType;
		this.candidateForm = value as UntypedFormGroup;
		this.ensureSessionFormStructure();
		// Trigger change detection to update filteredRankOptions
		this.cd.detectChanges();
	}

	@Input("sessions")
	set sessionsInput(value: any) {
		this.sessions = value;
		// Ensure formType is set from service
		if (!this.formType) {
			this.formType = this.containerRatingService.formType;
		}
		this.rebuildSessionLayoutFromInput();
		this.ensureSessionFormStructure();
		this.wireUpNotRequiredMutualExclusion();
		this.cd.detectChanges();
	}

	// -------- Lifecycle --------

	ngOnInit(): void {
		// Ensure formType is set from service
		if (!this.formType) {
			this.formType = this.containerRatingService.formType;
		}

		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
		});

		this.parentForm = this.candidateForm.parent as UntypedFormArray;
		this.parentFormArrayIndex =
			this.parentForm?.controls?.findIndex?.((subform) => subform === this.candidateForm) ?? 0;

		this.staffNumber.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
			clearStaffLookupFailureOnValueChange(this.staffNumber);
			this.rejectOwnStaffNumber();
		});
		this.rejectOwnStaffNumber();

		// Keep datepicker input in sync with licenseExpiryDate,
		// but do NOT re-patch licenseExpiryDate here (avoid loops)
		this.licenseExpiryDate.valueChanges
			.pipe(
				distinctUntilChanged(),
				skip(1), // ignore the first emission that often comes from initial patch
			)
			.subscribe((a) => {
				if (this.isBootstrapping) return;
				if (!a) return;

				const date = moment(a).toDate();
				this.datePickerInput.patchValue(date, { emitEvent: false });
				this.updateDisplayDateFormat(date);
			});

		// Initial setup is done now — mark pristine & end bootstrap
		this.candidateForm.markAsPristine();
		this.isBootstrapping = false;
	}

	// -------- Layout helpers --------

	private splitIntoColumns<T>(items: T[], columns: number): T[][] {
		if (columns <= 0) return [];
		if (columns === 1) return [items.slice()];

		if (columns === 2) {
			const n = items.length;
			// Even-ish split, but first column must have at least 5 (if available)
			const firstCount = Math.min(n, Math.max(5, Math.ceil(n / 2)));
			return [items.slice(0, firstCount), items.slice(firstCount)];
		}

		// Fallback for 3+ columns (simple round-robin or keep your previous logic)
		const cols: T[][] = Array.from({ length: columns }, () => []);
		items.forEach((item, idx) => cols[idx % columns].push(item));
		return cols;
	}

	private teardownSessionSubscriptions() {
		if (this.notReqSub) {
			this.notReqSub.unsubscribe();
			this.notReqSub = undefined;
		}
		this.otherSessionSubs.forEach((s) => s.unsubscribe());
		this.otherSessionSubs = [];
	}

	private findNotRequiredControl(): UntypedFormControl | null {
		const sd = this.sessionsDetails;
		if (!sd) return null;
		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;
			const c = grp.get("not_required") as UntypedFormControl | null;
			if (c) return c;
		}
		return null;
	}

	private wireUpNotRequiredMutualExclusion() {
		this.teardownSessionSubscriptions();
		const sd = this.sessionsDetails;
		if (!sd) return;

		const notReq = this.findNotRequiredControl();
		if (!notReq) return;

		// If not_required → true, uncheck everything else (in all groups)
		this.notReqSub = notReq.valueChanges.subscribe((val: boolean) => {
			if (val !== true) return;
			for (const g of this.sessionGroups) {
				const grp = sd.get(g.code) as UntypedFormGroup | null;
				if (!grp) continue;
				Object.keys(grp.controls).forEach((k) => {
					if (k === "not_required") return;
					const c = grp.get(k) as UntypedFormControl;
					if (c?.value === true) c.setValue(false, { emitEvent: false });
				});
			}
			// fire once after batch changes
			sd.updateValueAndValidity({ emitEvent: true });
		});

		// If any other session → true, force not_required → false
		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;
			Object.keys(grp.controls).forEach((k) => {
				if (k === "not_required") return;
				const c = grp.get(k) as UntypedFormControl;
				const sub = c.valueChanges.subscribe((val: boolean) => {
					if (val === true && notReq.value === true) {
						notReq.setValue(false, { emitEvent: false });
						sd.updateValueAndValidity({ emitEvent: true });
					}
				});
				this.otherSessionSubs.push(sub);
			});
		}
	}

	/** Convert raw `sessions` input to view-model groups and compute columns. */
	private rebuildSessionLayoutFromInput(): void {
		console.log("session group", this.sessions);
		// Push "others" group to the end (unchanged behavior you wanted earlier)
		const isOthers = (s: any) => s.name?.code?.toLowerCase() === "other";

		const sessionsRaw = this.sessions || [];
		const sessions = [...sessionsRaw.filter((s: any) => !isOthers(s)), ...sessionsRaw.filter(isOthers)];

		const groups: SessionGroupVM[] = (sessions ?? []).map((g: any) => ({
			code: g?.name?.code ?? "group",
			key: g?.name?.description ?? "Group",
			items: (g?.details ?? []).map((d: any) => ({
				code: d?.code,
				description: d?.description,
			})),
		}));

		this.sessionGroups = groups;

		if (this.sessionGroups.length === 1) {
			this.singleCols = this.splitIntoColumns(this.sessionGroups[0].items, 4);
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		} else if (this.sessionGroups.length === 4) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const lastItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
			this.lastCols = this.splitIntoColumns(lastItems, 1);
			this.lastColTitle = this.sessionGroups[2].key;
		} else if (this.sessionGroups.length == 3) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const lastItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
			this.lastCols = this.splitIntoColumns(lastItems, 1);
			this.lastColTitle = this.sessionGroups[2].key;
		} else {
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		}

		this.wireUpNotRequiredMutualExclusion();
	}

	/**
	 * Ensure the nested FormGroup structure exists:
	 * candidateForm.sessionDetails.<groupCode>.<detailCode>: boolean
	 * Build/remove controls silently to avoid valueChanges on load.
	 */
	private ensureSessionFormStructure(): void {
		if (!this.candidateForm) return;

		const sd = this.sessionsDetails;
		if (!sd) return;

		const variants = (code: string) => {
			const c = (code ?? "").trim();
			const set = new Set([c, c.replace(/\//g, "_"), c.replace(/_/g, "/")]);
			return Array.from(set);
		};

		for (const g of this.sessionGroups) {
			if (!sd.get(g.code)) {
				sd.addControl(g.code, new UntypedFormGroup({}), { emitEvent: false });
			}
			const grp = sd.get(g.code) as UntypedFormGroup;

			for (const item of g.items) {
				const key = item.code;
				const alts = variants(key);

				// If the exact key doesn't exist, look for an alias (e.g. LPC_OPC vs LPC/OPC)
				if (!grp.get(key)) {
					const aliasKey = alts.find((k) => !!grp.get(k));
					if (aliasKey) {
						const val = (grp.get(aliasKey) as UntypedFormControl).value;
						grp.removeControl(aliasKey, { emitEvent: false });
						grp.addControl(key, new UntypedFormControl(val), { emitEvent: false });
					} else {
						grp.addControl(key, new UntypedFormControl(false), { emitEvent: false });
					}
				}
			}

			// Remove any stale/alias controls that aren't in the VM list
			const validKeys = new Set(g.items.map((i) => i.code));
			Object.keys(grp.controls).forEach((k) => {
				if (!validKeys.has(k)) grp.removeControl(k, { emitEvent: false });
			});
		}

		sd.updateValueAndValidity({ emitEvent: false });
		this.candidateForm.updateValueAndValidity({ emitEvent: false });
	}

	// -------- Datepicker helpers --------

	updateDisplayDateFormat(date: Date) {
		const datePicker = this.datePickerRef?.nativeElement;
		if (datePicker && date !== null) {
			const formatted = moment(date).format(this.DATE_FORMAT.LICENSE_DATE_FORMAT);
			datePicker.value = formatted;
			this.cd.detectChanges();
		}
	}

	get filteredRankOptions() {
		return this.sharedService.getFilteredRankOptions(this.formType);
	}

	onDatePickerInit(datePickerRef?: ElementRef) {
		this.datePickerRef = datePickerRef;
	}

	handleDateChange(dateString: string): void {
		if (!isEmpty(dateString)) {
			// Programmatic save should be silent when bootstrapping
			this.saveLicenseExpiryDate(dateString, !this.isBootstrapping);
		}
	}

	// -------- Staff ID lookup --------

	onFocusOut(_: any) {
		this.candidateForm.markAsTouched();
		this.isOnline = navigator.onLine;
		if (this.rejectOwnStaffNumber()) return;

		this.cvs.validateStaffId(this.staffNumber);

		if (this.staffNumber.valid && this.staffNumber.value?.length === 7) {
			if (!navigator.onLine) return;

			this.loaderService.setLoading(true);
			this.httpService.getByStaffNumber(this.staffNumber.value, staffLookupRolesForForm(this.formType)).subscribe({
				next: this.onSuccessStaffId.bind(this),
				error: this.onErrorStaffId.bind(this),
			});
		}
	}

	private rejectOwnStaffNumber(): boolean {
		const rejected = rejectInstructorOwnStaffNumber(this.candidateForm, this.profileService.profile?.staff_number);
		if (rejected) {
			this.loaderService.setLoading(false);
			this.cd.detectChanges();
		}
		return rejected;
	}

	onSuccessStaffId(resp: { data: StaffDetails }) {
		const staffDetail = resp.data;

		const rankOption = this.sharedService.dropDownOptionsRank.find((v) => v.code === staffDetail.rank) ?? "";

		const licenseTypeOption =
			this.sharedService.getLicenseTypeOptions(this.formType)?.find((v) => v.code === staffDetail.licenseType) ?? "";

		// Silent programmatic patches to avoid autosave
		this.name.patchValue(staffDetail.name, { emitEvent: false });

		if (isEmpty(this.rank.value)) {
			this.rank.patchValue(rankOption, { emitEvent: false });
		}
		if (isEmpty(this.licenseNo.value)) {
			this.licenseNo.patchValue(staffDetail.licenseNumber, { emitEvent: false });
		}
		if (isEmpty(this.licenseType.value)) {
			this.licenseType.setValue(licenseTypeOption, { emitEvent: true });
		}
		patchStaffLicenseExpiryDate(this.licenseExpiryDate, staffDetail.licenseExpiryDate);

		// Validity recompute, silent
		this.candidateForm.updateValueAndValidity({ emitEvent: false });

		this.datePickerInput.markAllAsTouched();
		this.candidateForm.markAsTouched();
		this.loaderService.setLoading(false);
		this.cd.detectChanges();
	}

	onErrorStaffId(resp: any) {
		if (resp?.includes?.("No profile found")) {
			this.name.patchValue("", { emitEvent: false });
			this.rank.patchValue("", { emitEvent: false });
			this.licenseNo.patchValue("", { emitEvent: false });
			this.staffNumber?.setErrors({ invalidStaffId: true });
		}
		this.loaderService.setLoading(false);
	}

	ngOnDestroy(): void {
		this.teardownSessionSubscriptions();
	}

	// -------- Simple setters (emit only on user actions) --------

	private patchUserIntent<T = any>(ctrl: UntypedFormControl, value: T) {
		ctrl.patchValue(value, { emitEvent: !this.isBootstrapping });
	}

	saveName(value: string): void {
		this.patchUserIntent(this.name as any, value);
	}
	saveRank(value: string): void {
		this.patchUserIntent(this.rank as any, value);
	}
	saveLicenseType(value: string): void {
		this.patchUserIntent(this.licenseType as any, value);
	}
	saveStaffNumber(value: string): void {
		this.patchUserIntent(this.staffNumber as any, value);
	}
	savePilotProfile(value: string): void {
		this.patchUserIntent(this.pilotProfile as any, value);
	}
	saveLicenseNo(value: string): void {
		this.patchUserIntent(this.licenseNo as any, value);
	}
	saveLicenseExpiryDate(value: string, emit = true): void {
		this.licenseExpiryDate.patchValue(value, { emitEvent: emit && !this.isBootstrapping });
	}

	// -------- Form getters --------

	get name() {
		return this.candidateForm.get("name")!;
	}
	get rank() {
		return this.candidateForm.get("rank")!;
	}
	get staffNumber() {
		return this.candidateForm.get("staffNumber")!;
	}
	get pilotProfile() {
		return this.candidateForm.get("pilotProfile")!;
	}
	get licenseType() {
		return this.candidateForm.get("licenseType")!;
	}
	get licenseNo() {
		return this.candidateForm.get("licenseNo")!;
	}
	get licenseExpiryDate() {
		return this.candidateForm.get("licenseExpiryDate")! as UntypedFormControl;
	}

	get sessionsDetails(): UntypedFormGroup {
		return this.candidateForm.get("sessionDetails") as UntypedFormGroup;
	}

	// Utility if needed elsewhere
	getSessionControl(sessionCode: string): UntypedFormControl | null {
		const control = this.candidateForm.get("sessionDetails." + sessionCode);
		return control instanceof UntypedFormControl ? control : null;
	}
}
